import React from 'react'
import { createPortal } from 'react-dom'
import { ExclamationTriangleIcon, XMarkIcon } from '@heroicons/react/24/outline'
import Button from '../../ui/Button'

export interface AlxWeldIdMismatchDialogProps {
  open: boolean
  floorSheetWeldId: string
  detectedWeldId: string
  onChangeWeldId: () => void
  onCreateNewWeld: () => void
  onClose: () => void
}

export default function AlxWeldIdMismatchDialog({
  open,
  floorSheetWeldId,
  detectedWeldId,
  onChangeWeldId,
  onCreateNewWeld,
  onClose
}: AlxWeldIdMismatchDialogProps) {
  if (!open) return null

  const content = (
    <div
      className="fixed inset-0 z-[10010] flex items-center justify-center bg-blueprint-blue/40 backdrop-blur-sm px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="alx-weld-id-mismatch-title"
      data-testid="alx-weld-id-mismatch-dialog"
    >
      <div className="relative flex w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-[#E2E5EA] bg-white shadow-large">
        <div className="flex items-center gap-3 border-b border-[#E2E5EA] bg-page-bg px-6 py-4">
          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-warning-50 text-warning-700 ring-1 ring-inset ring-warning-200">
            <ExclamationTriangleIcon className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              id="alx-weld-id-mismatch-title"
              className="font-heading text-lg font-semibold text-blueprint-blue"
            >
              Weld ID mismatch
            </h2>
            <p className="text-xs text-heritage-grey">
              The uploaded folder belongs to a different weld than this Floor Sheet row.
            </p>
          </div>
          <button
            type="button"
            className="-mr-2 rounded-full p-2 text-heritage-grey transition-colors hover:bg-white hover:text-blueprint-blue"
            onClick={onClose}
            aria-label="Close"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-3 px-6 py-5 text-sm text-text-grey">
          <p>
            Floor Sheet Weld ID: <strong>{floorSheetWeldId || '—'}</strong>
          </p>
          <p>
            Folder Weld ID: <strong>{detectedWeldId}</strong>
          </p>
          <p className="text-xs text-heritage-grey">
            Change the Weld ID on this row to match the folder, or create a new weld on the Floor
            Sheet for the detected ID.
          </p>
        </div>

        <div className="flex flex-col gap-2 border-t border-[#E2E5EA] bg-page-bg px-6 py-4 sm:flex-row sm:justify-end">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" onClick={onChangeWeldId} data-testid="alx-weld-id-change-row">
            Change Weld ID here
          </Button>
          <Button type="button" onClick={onCreateNewWeld} data-testid="alx-weld-id-create-weld">
            Create new weld
          </Button>
        </div>
      </div>
    </div>
  )

  return createPortal(content, document.body)
}
